import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../assets/Images/logo-tran.png';
import LanguageSwitcher from './LanguageSwitcher';

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const servicesDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' && !activeSection;
    }
    return location.pathname === path;
  };

  const navLinks = useMemo(() => [
    { path: '/', label: t('navigation.home'), section: 'home' },
    { path: '/about', label: t('navigation.about'), section: 'about' },
    { path: '/gallery', label: t('navigation.gallery'), section: 'gallery' },
    { path: '/services', label: t('navigation.services'), section: 'services' },
    { path: '/contact', label: t('navigation.contact'), section: 'contact' },
  ], [t]);

  const servicesLinks = [
    { path: '/services/custom-design', label: t('navigation.customDesign') },
    { path: '/services/restoration', label: t('navigation.restoration') },
    { path: '/services/mold-design', label: t('navigation.moldDesign') },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      // Only check for sections if we're on the home page
      if (location.pathname === '/') {
        const sections = navLinks.map(link => ({
          id: link.section,
          element: document.getElementById(link.section)
        }));

        // Find the last section whose top is above the threshold
        let lastVisibleSection = '';
        sections.forEach(section => {
          if (section.element) {
            const rect = section.element.getBoundingClientRect();
            if (rect.top <= 100) {
              lastVisibleSection = section.id;
            }
          }
        });

        if (lastVisibleSection) {
          if (lastVisibleSection === 'achievements') {
            setActiveSection('contact');
          } else {
            setActiveSection(lastVisibleSection);
          }
        } else {
          setActiveSection('');
        }
      } else {
        // If we're not on the home page, clear the active section
        setActiveSection('');
      }
    };

    // Initial check
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, navLinks]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target as Node)) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white shadow-md' 
        : 'bg-black bg-opacity-50 backdrop-blur-sm'
    }`}>
      <div className="w-full">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src={logo} 
                  alt="BETA Logo" 
                  className={`h-10 w-auto ${isRTL ? 'ml-2' : 'mr-2'}`}
                />
                <span className={`text-2xl font-bold ${
                  isScrolled ? 'text-gray-800' : 'text-white'
                }`}>
                  BETA
                </span>
              </Link>
            </div>
            <div className={`hidden sm:flex ${isRTL ? 'sm:mr-8 space-x-reverse space-x-4' : 'sm:ml-8 space-x-4'}`}>
              {navLinks.filter(link => link.path !== '/contact' && link.path !== '/services').map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                    (isActive(link.path) || (location.pathname === '/' && activeSection === link.section))
                      ? isScrolled
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-white bg-opacity-20 text-white'
                      : isScrolled
                        ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                        : 'text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Services Dropdown */}
              <div className="relative" ref={servicesDropdownRef}>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                    (activeSection === 'services' || servicesLinks.some(s => location.pathname === s.path))
                      ? isScrolled
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-white bg-opacity-20 text-white'
                      : isScrolled
                        ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                        : 'text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  {t('navigation.services')}
                  <svg
                    className={`ml-1 h-4 w-4 transition-transform duration-200 ${
                      isServicesOpen ? 'transform rotate-180' : ''
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                {isServicesOpen && (
                  <div className={`absolute left-0 mt-2 w-64 rounded-md shadow-lg py-1 ${
                    isScrolled ? 'bg-white ring-1 ring-black ring-opacity-5' : 'bg-black bg-opacity-90 backdrop-blur-sm'
                  }`}>
                    {servicesLinks.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        className={`block px-4 py-2 text-sm ${
                          isScrolled
                            ? 'text-gray-700 hover:bg-gray-100'
                            : 'text-white hover:bg-white hover:bg-opacity-10'
                        }`}
                        onClick={() => setIsServicesOpen(false)}
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {/* Contact link at the end */}
              <Link
                key="/contact"
                to="/contact"
                className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                  (isActive('/contact') || (location.pathname === '/' && (activeSection === 'contact' || activeSection === 'achievements')))
                    ? isScrolled
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-white bg-opacity-20 text-white'
                    : isScrolled
                      ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                      : 'text-white hover:bg-white hover:bg-opacity-10'
                }`}
              >
                {t('navigation.contact')}
              </Link>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher isScrolled={isScrolled} />
          </div>
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`inline-flex items-center justify-center p-2 rounded-md ${
                isScrolled
                  ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  : 'text-white hover:bg-white hover:bg-opacity-10'
              } focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500`}
            >
              <span className="sr-only">Open main menu</span>
              {!isMenuOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden ${isMenuOpen ? 'block' : 'hidden'}`}>
        <div className="px-2 pt-2 pb-3 space-y-1">
          {navLinks.filter(link => link.path !== '/contact' && link.path !== '/services').map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                (isActive(link.path) || (location.pathname === '/' && activeSection === link.section))
                  ? isScrolled
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-white bg-opacity-20 text-white'
                  : isScrolled
                    ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                    : 'text-white hover:bg-white hover:bg-opacity-10'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          
          {/* Mobile Services Dropdown */}
          <div>
            <button
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                (activeSection === 'services' || servicesLinks.some(s => location.pathname === s.path))
                  ? isScrolled
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-white bg-opacity-20 text-white'
                  : isScrolled
                    ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                    : 'text-white hover:bg-white hover:bg-opacity-10'
              }`}
            >
              <div className="flex justify-between items-center">
                <span>{t('navigation.services')}</span>
                <svg
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isServicesOpen ? 'transform rotate-180' : ''
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </button>
            
            {/* Mobile Dropdown Menu */}
            {isServicesOpen && (
              <div className="pl-4 space-y-1">
                {servicesLinks.map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    className={`block px-3 py-2 rounded-md text-base font-medium ${
                      isScrolled
                        ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                        : 'text-white hover:bg-white hover:bg-opacity-10'
                    }`}
                    onClick={() => {
                      setIsServicesOpen(false);
                      setIsMenuOpen(false);
                    }}
                  >
                    {service.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
          
          {/* Contact link at the end */}
          <Link
            key="/contact"
            to="/contact"
            className={`block px-3 py-2 rounded-md text-base font-medium ${
              (isActive('/contact') || (location.pathname === '/' && (activeSection === 'contact' || activeSection === 'achievements')))
                ? isScrolled
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-white bg-opacity-20 text-white'
                : isScrolled
                  ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  : 'text-white hover:bg-white hover:bg-opacity-10'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('navigation.contact')}
          </Link>
          
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="px-2 py-2">
              <LanguageSwitcher isScrolled={isScrolled} />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation; 